import React, { useEffect, useState } from "react";
import { BarChart3 } from "lucide-react";
import { api } from "@/lib/api";
import { StockMenu } from "@/components/WatchAdd";

const sign = (v) => (v > 0 ? "+" : "");
const pct = (v) => (v == null ? "—" : `${sign(v)}${v.toFixed(2)}%`);
const pts = (v) => (v == null ? "—" : `${sign(v)}${v.toFixed(1)} pts`);
const NAMES = { NIFTY: "NIFTY 50", BANKNIFTY: "BANK NIFTY" };

const Bar = ({ s, max, mode, idx }) => {
  const v = mode === "pct" ? s.change_pct : s.points;
  const w = v == null || !max ? 0 : Math.min(Math.abs(v) / max, 1) * 50;
  const up = (v || 0) >= 0;
  return (
    <StockMenu instrumentId={s.instrument_id} symbol={s.symbol}>
      <button className="grid w-full grid-cols-12 items-center gap-2 px-2 py-1.5 text-xs font-mono hover:bg-blue-500/5 transition-colors text-left" data-testid={`idx-bar-${idx}-${s.symbol}`}>
        <span className="col-span-3 truncate font-semibold text-slate-900">{s.symbol}<span className="ml-1 text-[9px] font-normal text-slate-400">{s.weight}%</span></span>
        <span className="relative col-span-6 h-6">
          <span className="absolute inset-y-0 left-1/2 w-px bg-slate-300" />
          <span className={`absolute inset-y-1 rounded-sm transition-[width] duration-500 ${up ? "bg-emerald-500" : "bg-rose-500"}`}
            style={up ? { left: "50%", width: `${w}%` } : { right: "50%", width: `${w}%` }} />
        </span>
        <span className="col-span-3 text-right leading-tight">
          <span className={`block font-semibold ${up ? "text-emerald-600" : "text-rose-600"}`}>{mode === "pct" ? pct(s.change_pct) : pts(s.points)}</span>
          <span className="block text-[9px] text-slate-500">{mode === "pct" ? pts(s.points) : pct(s.change_pct)}</span>
        </span>
      </button>
    </StockMenu>
  );
};

const IndexCard = ({ d, mode }) => {
  const vals = d.stocks.map((s) => Math.abs((mode === "pct" ? s.change_pct : s.points) || 0));
  const max = Math.max(...vals, 0.01);
  const adds = d.stocks.reduce((a, s) => a + (s.points || 0), 0);
  const up = (d.change_pct || 0) >= 0;
  return (
    <div className="border border-slate-200 bg-white" data-testid={`idx-card-${d.index}`}>
      <div className="flex items-end justify-between border-b border-slate-200 px-4 py-3">
        <div>
          <div className="text-[10px] font-mono tracking-widest text-slate-500">{NAMES[d.index] || d.index} · FUT</div>
          <div className="font-display text-2xl font-bold text-slate-900" data-testid={`idx-ltp-${d.index}`}>{d.ltp?.toLocaleString("en-IN") ?? "—"}</div>
        </div>
        <div className="text-right font-mono">
          <div className={`text-lg font-bold ${up ? "text-emerald-600" : "text-rose-600"}`} data-testid={`idx-chg-${d.index}`}>{pct(d.change_pct)}</div>
          <div className="text-[11px] text-slate-500">{pts(d.points)} · top 10 {pts(adds)}</div>
        </div>
      </div>
      <div className="py-1">{d.stocks.map((s) => <Bar key={s.symbol} s={s} max={max} mode={mode} idx={d.index} />)}</div>
    </div>
  );
};

export default function Indices() {
  const [d, setD] = useState(null);
  const [mode, setMode] = useState("pct");
  useEffect(() => {
    const load = () => api.get("/today/indices").then((r) => setD(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="space-y-4" data-testid="indices-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><BarChart3 className="h-5 w-5 text-blue-600" /> Indices</h1>
          <p className="text-xs text-slate-500 mt-0.5 font-mono">top 10 stocks by index weight · bar = today's move · points = approx index points added · weights approximate</p>
        </div>
        <div className="flex border border-slate-200 text-xs font-mono" data-testid="idx-mode">
          {[["pct", "% change"], ["pts", "Points"]].map(([k, l]) => (
            <button key={k} onClick={() => setMode(k)} className={`px-3 py-1.5 transition-colors ${mode === k ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`idx-mode-${k}`}>{l}</button>
          ))}
        </div>
      </div>
      {!d ? <p className="p-6 text-xs font-mono text-slate-500">Loading…</p> : (
        <div className="grid gap-4 lg:grid-cols-2">{d.indices.map((x) => <IndexCard key={x.index} d={x} mode={mode} />)}</div>
      )}
    </div>
  );
}
